import type {CSSProperties} from 'react';
import {useState} from 'react';

import type {DraggedShares, PaneBounds, PaneOpenState, PaneShares, SidePane} from './paneShares';
import {clampShare, getShareLimits, resolvePaneShares} from './paneShares';

const NO_DRAGS: DraggedShares = {list: null, panel: null};

/**
 * Session-only widths for the fused card's side panes. A dragged share sticks until its pane
 * closes; every other pane follows the default split for whichever panes are open.
 * `bounds` holds the side panes' floors and the list's opening width, as shares of the card.
 */
export const usePaneWidths = (open: PaneOpenState, bounds?: PaneBounds) => {
  const [dragged, setDragged] = useState<DraggedShares>(NO_DRAGS);
  const [draggingPane, setDraggingPane] = useState<SidePane | null>(null);
  const [prevOpen, setPrevOpen] = useState(open);

  if (prevOpen.isListOpen !== open.isListOpen || prevOpen.isPanelOpen !== open.isPanelOpen) {
    setPrevOpen(open);
    // Forget a dragged share on reopen, not on close, so a closing pane keeps its width.
    setDragged((current) => ({
      list: open.isListOpen && !prevOpen.isListOpen ? null : current.list,
      panel: open.isPanelOpen && !prevOpen.isPanelOpen ? null : current.panel
    }));
  }

  const shares = resolvePaneShares(open, dragged, bounds);
  // A closed pane keeps the width it would open to, so its content doesn't reflow while its
  // track animates to or from 0.
  const contentShares: PaneShares = {
    list: resolvePaneShares({...open, isListOpen: true}, dragged, bounds).list,
    panel: resolvePaneShares({...open, isPanelOpen: true}, dragged, bounds).panel
  };

  const resize = (pane: SidePane, share: number) => {
    setDragged((current) => ({...current, [pane]: clampShare(pane, share, shares, bounds)}));
  };

  const reset = (pane: SidePane) => {
    setDragged((current) => ({...current, [pane]: null}));
  };

  // Set as plain properties rather than inherited custom properties: changing a custom property on
  // the card restyles every element inside it, on every pointer move of a drag.
  const cardStyle: CSSProperties = {
    gridTemplateColumns: `${shares.list}% 0px minmax(0, 1fr) 0px ${shares.panel}%`
  };
  const contentStyle = (pane: SidePane): CSSProperties => ({width: `${contentShares[pane]}cqw`});

  return {
    shares,
    contentShares,
    cardStyle,
    contentStyle,
    getLimits: (pane: SidePane) => getShareLimits(pane, shares, bounds),
    resize,
    reset,
    draggingPane,
    setDraggingPane
  };
};
